import React, { useLayoutEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Copy, Fish, MapPin, Plus } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { BaitPicker, BaitSelection } from "../components/BaitPicker";
import { RigPicker, RigSelection } from "../components/RigPicker";
import { LeadSystemPicker, LeadSystemSelection, emptyLeadSystem } from "../components/LeadSystemPicker";
import { formatDateTime } from "../lib/format";

const emptyRig = (): RigSelection => ({ canonical_id: null, user_rig_id: null, snapshot: "" });
const emptyBait = (): BaitSelection => ({ canonical_id: null, user_bait_id: null, snapshot: "" });
const FEED_UNITS = ["grams", "kilograms", "boilies", "handfuls", "PVA bags", "stringers", "ml"];
const PRESENTATIONS = ["bottom bait", "wafter", "pop-up", "zig foam", "topper", "snowman component", "other"];

type HookComponent = {
  bait: BaitSelection;
  size_mm: string;
  presentation: string;
  colour: string;
};
type FeedLine = { bait: BaitSelection; amount: string; unit: string };

type EditorState =
  | { key: string; mode: "lead" | "rig" | "hookbait" | "feed" | "position" | "copy"; anglerId: number; rod: number }
  | null;

function stateKey(anglerId: number, rod: number, component: string) {
  return `${anglerId}:${rod}:${component}`;
}

function hookSummary(components: HookComponent[]) {
  return components
    .filter((component) => component.bait.snapshot.trim())
    .map((component) =>
      [component.bait.snapshot, component.size_mm ? `${component.size_mm}mm` : "", component.presentation, component.colour]
        .filter(Boolean)
        .join(" "),
    )
    .join(" + ");
}

function feedSummary(event: any) {
  const mix = Array.isArray(event?.mix_json) ? event.mix_json : [];
  if (!mix.length && String(event?.notes ?? "").toLowerCase().includes("no loose feed")) return "No loose feed";
  return mix
    .map((item: any) => [item.snapshot, item.amount ? `${item.amount} ${item.unit || ""}` : ""].filter(Boolean).join(" · "))
    .filter(Boolean)
    .join(" + ");
}

function sameSpot(a: any, b: any) {
  if (!a || !b) return false;
  const aId = Number(a.spot_id || 0);
  const bId = Number(b.spot_id || 0);
  if (aId && bId) return aId === bId;
  const aName = String(a.spot_snapshot ?? "").trim().toLowerCase();
  const bName = String(b.spot_snapshot ?? "").trim().toLowerCase();
  return Boolean(aName && bName && aName === bName);
}

function feedAge(value: unknown) {
  const at = new Date(String(value ?? "")).getTime();
  if (!Number.isFinite(at)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - at) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function LiveSessionPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () => fetch(`/app-api/sessions/${id}`).then((r) => {
      if (!r.ok) throw new Error("Session not found");
      return r.json();
    }),
  });
  const anglersQuery = useQuery({
    queryKey: ["carpintel_session_anglers", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/anglers`).then((r) => (r.ok ? r.json() : [])),
    enabled: Boolean(id),
  });
  const stateQuery = useQuery({
    queryKey: ["bankside_rod_state", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/rod-state`).then((r) => (r.ok ? r.json() : { states: [], feed_events: [], positions: [] })),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
  const setupsQuery = useQuery({
    queryKey: ["carpintel_session_rod_setups", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/rod-setups`).then((r) => (r.ok ? r.json() : [])),
    enabled: Boolean(id),
  });

  const session = sessionQuery.data;
  const anglers: any[] = anglersQuery.data ?? [];
  const stateRows: any[] = stateQuery.data?.states ?? [];
  const feedEvents: any[] = stateQuery.data?.feed_events ?? [];
  const positions: any[] = stateQuery.data?.positions ?? [];
  const setups: any[] = setupsQuery.data ?? [];

  const [editor, setEditor] = useState<EditorState>(null);
  const [lead, setLead] = useState<LeadSystemSelection>(emptyLeadSystem());
  const [rig, setRig] = useState<RigSelection>(emptyRig());
  const [hookComponents, setHookComponents] = useState<HookComponent[]>([
    { bait: emptyBait(), size_mm: "", presentation: "", colour: "" },
  ]);
  const [feedLines, setFeedLines] = useState<FeedLine[]>([
    { bait: emptyBait(), amount: "", unit: "grams" },
  ]);
  const [feedRodRefs, setFeedRodRefs] = useState<string[]>([]);
  const [positionSpot, setPositionSpot] = useState("");
  const [positionText, setPositionText] = useState("");
  const [copyTargets, setCopyTargets] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const spotsQuery = useQuery({
    queryKey: ["carpintel_saved_spots", session?.lake_id],
    queryFn: () => session?.lake_id
      ? fetch(`/app-api/lakes/${session.lake_id}/spots`).then((r) => (r.ok ? r.json() : []))
      : Promise.resolve([]),
    enabled: Boolean(session?.lake_id),
  });
  const spots: any[] = spotsQuery.data ?? [];

  const stateMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of stateRows) map.set(stateKey(Number(row.session_angler_id), Number(row.rod_number), String(row.component_type)), row);
    return map;
  }, [stateRows]);
  const positionMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of positions) map.set(`${row.session_angler_id}:${row.rod_number}`, row);
    return map;
  }, [positions]);
  const setupMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of setups) map.set(`${row.session_angler_id}:${row.rod_number}`, row);
    return map;
  }, [setups]);
  const latestSpotFeedMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const event of feedEvents) {
      for (const ref of Array.isArray(event.rods) ? event.rods : []) {
        const key = `${ref.session_angler_id}:${ref.rod_number}`;
        if (map.has(key)) continue;
        const position = positionMap.get(key);
        if (sameSpot(event, position)) map.set(key, event);
      }
    }
    return map;
  }, [feedEvents, positionMap]);

  const rodRefs = useMemo(() => anglers.flatMap((angler) =>
    Array.from({ length: Math.max(1, Number(angler.rods_count || 1)) }, (_, index) => ({
      anglerId: Number(angler.id), rod: index + 1, label: anglers.length > 1 ? `${angler.display_name} · Rod ${index + 1}` : `Rod ${index + 1}`,
    })),
  ), [anglers]);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const caughtRod = Number(query.get("rod") || 0);
  const caughtAngler = Number(query.get("angler") || 0);
  const showPostCatch = query.get("caught") === "1" && caughtRod > 0 && caughtAngler > 0;
  const editorScreenKey = editor ? `${editor.key}:${editor.mode}` : "live-board";

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [editorScreenKey]);

  function rodsSharingSpot(key: string) {
    const position = positionMap.get(key);
    if (!position) return [];
    return rodRefs.filter((ref) => sameSpot(positionMap.get(`${ref.anglerId}:${ref.rod}`), position));
  }

  function openEditor(mode: NonNullable<EditorState>["mode"], anglerId: number, rod: number) {
    const key = `${anglerId}:${rod}`;
    setEditor({ key, mode, anglerId, rod });
    setMessage("");
    if (mode === "lead") {
      const current = stateMap.get(stateKey(anglerId, rod, "lead_system"));
      setLead({ canonical_id: current?.canonical_ref_id ?? null, user_lead_system_id: current?.user_ref_id ?? null, snapshot: current?.snapshot ?? "" });
    }
    if (mode === "rig") {
      const current = stateMap.get(stateKey(anglerId, rod, "rig"));
      setRig({ canonical_id: current?.canonical_ref_id ?? null, user_rig_id: current?.user_ref_id ?? null, snapshot: current?.snapshot ?? "" });
    }
    if (mode === "hookbait") {
      const current = stateMap.get(stateKey(anglerId, rod, "hookbait"));
      const details = current?.details_json && typeof current.details_json === "object" ? current.details_json : {};
      const components = Array.isArray(details.components) ? details.components : [];
      setHookComponents(components.length ? components.map((item: any) => ({
        bait: { canonical_id: item?.bait?.canonical_id ?? null, user_bait_id: item?.bait?.user_bait_id ?? null, snapshot: item?.bait?.snapshot ?? "" },
        size_mm: item?.size_mm == null ? "" : String(item.size_mm),
        presentation: String(item?.presentation ?? ""),
        colour: String(item?.colour ?? ""),
      })) : [{ bait: emptyBait(), size_mm: "", presentation: "", colour: "" }]);
    }
    if (mode === "feed") {
      const current = latestSpotFeedMap.get(key);
      const mix = Array.isArray(current?.mix_json) ? current.mix_json : [];
      setFeedLines(mix.length ? mix.map((item: any) => ({
        bait: { canonical_id: item?.canonical_id ?? null, user_bait_id: item?.user_bait_id ?? null, snapshot: item?.snapshot ?? "" },
        amount: item?.amount == null ? "" : String(item.amount), unit: item?.unit || "grams",
      })) : [{ bait: emptyBait(), amount: "", unit: "grams" }]);
      const sameSpotRods = rodsSharingSpot(key);
      setFeedRodRefs(sameSpotRods.length ? sameSpotRods.map((ref) => `${ref.anglerId}:${ref.rod}`) : [key]);
    }
    if (mode === "position") {
      const current = positionMap.get(key);
      setPositionSpot(current?.spot_id ? String(current.spot_id) : "");
      setPositionText(current?.spot_snapshot ?? "");
    }
    if (mode === "copy") setCopyTargets([]);
  }

  async function refreshState() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["bankside_rod_state", id] }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_session_rod_setups", id] }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_session_events", id] }),
      queryClient.invalidateQueries({ queryKey: ["catchcompass_rig_intelligence"] }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] }),
    ]);
  }

  async function saveComponent(component: "lead_system" | "rig" | "hookbait") {
    if (!editor) return;
    setBusy(true);
    setMessage("");
    try {
      let body: any = { session_angler_id: editor.anglerId, rod_number: editor.rod };
      if (component === "lead_system") body = { ...body, canonical_ref_id: lead.canonical_id, user_ref_id: lead.user_lead_system_id, snapshot: lead.snapshot };
      if (component === "rig") body = { ...body, canonical_ref_id: rig.canonical_id, user_ref_id: rig.user_rig_id, snapshot: rig.snapshot };
      if (component === "hookbait") {
        const components = hookComponents.filter((item) => item.bait.snapshot.trim());
        body = {
          ...body,
          canonical_ref_id: components[0]?.bait.canonical_id ?? null,
          user_ref_id: components[0]?.bait.user_bait_id ?? null,
          snapshot: hookSummary(components),
          details_json: { components },
        };
      }
      const response = await fetch(`/app-api/sessions/${id}/rod-state/${component}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save change");
      await refreshState();
      setEditor(null);
      setMessage(`Rod ${editor.rod} ${component.replace("_", " ")} updated. Everything else kept its own exposure clock.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save change");
    } finally { setBusy(false); }
  }

  async function saveFeed() {
    if (!editor) return;
    const position = positionMap.get(editor.key);
    if (!position?.spot_snapshot) { setMessage("This rod needs a current spot before you can top it up."); return; }
    const mix = feedLines.filter((line) => line.bait.snapshot.trim()).map((line) => ({
      ...line.bait, amount: line.amount ? Number(line.amount) : null, unit: line.unit,
    }));
    if (!mix.length) { setMessage("Add at least one feed bait."); return; }
    const sameSpotKeys = new Set(rodsSharingSpot(editor.key).map((ref) => `${ref.anglerId}:${ref.rod}`));
    const refs = rodRefs.filter((ref) => feedRodRefs.includes(`${ref.anglerId}:${ref.rod}`) && sameSpotKeys.has(`${ref.anglerId}:${ref.rod}`));
    if (!refs.length) { setMessage("Choose at least one rod fishing this spot."); return; }
    setBusy(true); setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/feed-events`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action_type: "top_up", mix, rods: refs.map((ref) => ({ session_angler_id: ref.anglerId, rod_number: ref.rod })), spot_id: position?.spot_id ?? null, spot_snapshot: position?.spot_snapshot ?? null }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not log feed top-up");
      await refreshState(); setEditor(null);
      setMessage(`Top-up logged once on ${position.spot_snapshot}${refs.length > 1 ? ` for ${refs.length} rods fishing that spot` : ""}.`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not log feed top-up"); }
    finally { setBusy(false); }
  }

  async function savePosition() {
    if (!editor) return;
    const selected = spots.find((spot) => String(spot.id) === positionSpot);
    const snapshot = selected?.name || positionText.trim();
    if (!snapshot) { setMessage("Choose or describe the new position."); return; }
    setBusy(true); setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/events`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_angler_id: editor.anglerId, rod_number: editor.rod, event_type: "rod_moved", spot_id: selected?.id ?? null, spot_snapshot: snapshot, distance_m: selected?.distance_m ?? null, depth_ft: selected?.depth_ft ?? null }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not move rod");
      await refreshState();
      setEditor(null);
      setMessage(`Rod ${editor.rod} moved to ${snapshot}. Feed on the old spot stays there; this spot now has its own feed history.`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not move rod"); }
    finally { setBusy(false); }
  }

  async function copyRod() {
    if (!editor || !copyTargets.length) return;
    setBusy(true); setMessage("");
    try {
      const targets = rodRefs.filter((ref) => copyTargets.includes(`${ref.anglerId}:${ref.rod}`)).map((ref) => ({ session_angler_id: ref.anglerId, rod_number: ref.rod }));
      const response = await fetch(`/app-api/sessions/${id}/copy-rod-state`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_session_angler_id: editor.anglerId, source_rod_number: editor.rod, targets }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not copy rod setup");
      await refreshState(); setEditor(null); setMessage(`Rod ${editor.rod} tackle copied to ${targets.length} rod${targets.length === 1 ? "" : "s"}. Spot feed was not copied.`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not copy rod setup"); }
    finally { setBusy(false); }
  }

  async function quickEvent(anglerId: number, rod: number, eventType: string) {
    setBusy(true); setMessage("");
    try {
      const setup = setupMap.get(`${anglerId}:${rod}`);
      const response = await fetch(`/app-api/sessions/${id}/events`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_angler_id: anglerId, rod_number: rod, event_type: eventType, rod_setup_id: setup?.id ?? null }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not log event");
      await refreshState(); setMessage(`Rod ${rod}: ${eventType.replace("_", " ")} logged against the tackle state that was active now.`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not log event"); }
    finally { setBusy(false); }
  }

  if (sessionQuery.isLoading || anglersQuery.isLoading) return <PageMessage text="Loading live session…" />;
  if (sessionQuery.error || !session) return <PageMessage text="That session could not be found." />;
  if (session.end_at) return <Navigate to={`/sessions/${id}`} replace />;

  return (
    <div className="mx-auto max-w-[760px] space-y-4 px-4 pt-4 pb-[calc(9rem+env(safe-area-inset-bottom))]">
      <header>
        <Link to="/app" className="inline-flex min-h-10 items-center text-sm font-medium text-accent">← Home</Link>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-accent">Live session</p>
            <h1 className="truncate text-xl font-semibold text-primary">{session.lake_name}</h1>
            <p className="mt-0.5 text-sm text-secondary">{session.peg || "Peg not selected"} · started {formatDateTime(session.start_at)}</p>
          </div>
          <Fish className="size-6 shrink-0 text-accent" />
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs text-secondary">
          <span className="rounded-full bg-inset px-2.5 py-1">Target: {session.target_species}</span>
          {session.planned_end_at ? <span className="rounded-full bg-inset px-2.5 py-1">Finish: {formatDateTime(session.planned_end_at)}</span> : null}
          <Link to={`/sessions/${id}`} className="rounded-full bg-inset px-2.5 py-1 text-accent">Session details</Link>
        </div>
      </header>

      {message ? <p className="rounded-lg border border-border bg-raised p-3 text-sm text-secondary" role="status">{message}</p> : null}

      {editor ? (
        <section className="rounded-2xl border border-accent/45 bg-raised p-4" aria-label="Live session action">
          <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">Rod {editor.rod}</p>
              <h2 className="mt-0.5 text-lg font-semibold text-primary">{editor.mode === "hookbait" ? "Change hookbait" : editor.mode === "feed" ? "Top up spot" : editor.mode === "position" ? "Move rod" : editor.mode === "copy" ? "Copy tackle setup" : editor.mode === "lead" ? "Change lead" : "Change rig"}</h2>
            </div>
            <button type="button" className="min-h-11 rounded-lg px-3 text-sm font-semibold text-secondary hover:bg-inset hover:text-primary" onClick={() => setEditor(null)}>Back to live</button>
          </div>
          <div className="mt-4">
            {editor.mode === "lead" ? <><LeadSystemPicker id="live-lead" value={lead} onChange={setLead} speciesFilter={session.target_species} /><Button className="mt-3 min-h-12 w-full" onClick={() => saveComponent("lead_system")} isLoading={busy}>Save lead system</Button></> : null}
            {editor.mode === "rig" ? <><RigPicker id="live-rig" label="Rig" value={rig} onChange={setRig} speciesFilter={session.target_species} /><Button className="mt-3 min-h-12 w-full" onClick={() => saveComponent("rig")} isLoading={busy}>Save rig</Button></> : null}
            {editor.mode === "hookbait" ? (
              <div className="space-y-4">
                {hookComponents.map((component, index) => (
                  <div key={index} className="space-y-2 rounded-xl border border-border bg-background p-3">
                    <BaitPicker id={`hook-${index}`} label={index === 0 ? "Hookbait" : `Hookbait component ${index + 1}`} value={component.bait} onChange={(bait) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, bait } : row))} speciesFilter={session.target_species} presentationRole="hookbait" />
                    <div className="grid grid-cols-3 gap-2">
                      <Input type="number" min="1" max="50" placeholder="mm" value={component.size_mm} onChange={(e) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, size_mm: e.target.value } : row))} />
                      <select className="h-11 rounded-lg border border-border bg-background px-2 text-xs text-primary" value={component.presentation} onChange={(e) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, presentation: e.target.value } : row))}><option value="">Presentation</option>{PRESENTATIONS.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                      <Input placeholder="Colour" value={component.colour} onChange={(e) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, colour: e.target.value } : row))} />
                    </div>
                    {hookComponents.length > 1 ? <button type="button" className="min-h-10 text-xs text-error" onClick={() => setHookComponents((current) => current.filter((_, i) => i !== index))}>Remove component</button> : null}
                  </div>
                ))}
                {hookComponents.length < 3 ? <Button type="button" size="sm" variant="tertiary" onClick={() => setHookComponents((current) => [...current, { bait: emptyBait(), size_mm: "", presentation: "", colour: "" }])}><Plus className="size-4" /> Add snowman / topper component</Button> : null}
                <Button className="min-h-12 w-full" onClick={() => saveComponent("hookbait")} isLoading={busy}>Save hookbait</Button>
              </div>
            ) : null}
            {editor.mode === "feed" ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-inset p-3 text-sm text-secondary">
                  <p className="font-semibold text-primary">{positionMap.get(editor.key)?.spot_snapshot || "Current spot"}</p>
                  <p className="mt-1">This records a new top-up on the spot. Moving the rod later will not carry this bait with it.</p>
                </div>
                {feedLines.map((line, index) => (
                  <div key={index} className="space-y-2 rounded-xl border border-border bg-background p-3">
                    <BaitPicker id={`feed-${index}`} label={`Feed ${index + 1}`} value={line.bait} onChange={(bait) => setFeedLines((current) => current.map((row, i) => i === index ? { ...row, bait } : row))} speciesFilter={session.target_species} presentationRole="feed" />
                    <div className="grid grid-cols-2 gap-2"><Input type="number" min="0" step="0.1" placeholder="Amount" value={line.amount} onChange={(e) => setFeedLines((current) => current.map((row, i) => i === index ? { ...row, amount: e.target.value } : row))} /><select className="h-11 rounded-lg border border-border bg-background px-3 text-sm text-primary" value={line.unit} onChange={(e) => setFeedLines((current) => current.map((row, i) => i === index ? { ...row, unit: e.target.value } : row))}>{FEED_UNITS.map((unit) => <option key={unit}>{unit}</option>)}</select></div>
                    {feedLines.length > 1 ? <button type="button" className="min-h-10 text-xs text-error" onClick={() => setFeedLines((current) => current.filter((_, i) => i !== index))}>Remove feed item</button> : null}
                  </div>
                ))}
                <Button type="button" size="sm" variant="tertiary" onClick={() => setFeedLines((current) => [...current, { bait: emptyBait(), amount: "", unit: "grams" }])}><Plus className="size-4" /> Add feed item</Button>
                <div><p className="text-sm font-medium text-primary">Rods fishing this spot</p><div className="mt-2 flex flex-wrap gap-2">{rodsSharingSpot(editor.key).map((ref) => { const key = `${ref.anglerId}:${ref.rod}`; const active = feedRodRefs.includes(key); return <button key={key} type="button" onClick={() => setFeedRodRefs((current) => active ? current.filter((item) => item !== key) : [...current, key])} className={`min-h-10 rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? "border-accent bg-accent text-accent-fg" : "border-border"}`}>{ref.label}</button>; })}</div></div>
                <Button className="min-h-12 w-full" onClick={saveFeed} isLoading={busy}>Save top-up to spot</Button>
              </div>
            ) : null}
            {editor.mode === "position" ? (
              <div className="space-y-3">
                <p className="text-sm text-secondary">Move this rod to a new fishing position. Feed already put on the old spot stays with the old spot.</p>
                {spots.length ? <select className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary" value={positionSpot} onChange={(e) => { setPositionSpot(e.target.value); if (e.target.value) setPositionText(""); }}><option value="">Choose saved spot</option>{spots.map((spot) => <option key={spot.id} value={spot.id}>{spot.name}</option>)}</select> : null}
                <Input value={positionText} disabled={Boolean(positionSpot)} onChange={(e) => setPositionText(e.target.value)} placeholder="Or describe position, e.g. left margin" />
                <Button className="min-h-12 w-full" onClick={savePosition} isLoading={busy}>Move rod</Button>
              </div>
            ) : null}
            {editor.mode === "copy" ? (
              <div className="space-y-3">
                <p className="text-sm text-secondary">Copy lead system, rig and hookbait from Rod {editor.rod}. Position and spot feed are deliberately not copied.</p>
                <div className="flex flex-wrap gap-2">{rodRefs.filter((ref) => !(ref.anglerId === editor.anglerId && ref.rod === editor.rod)).map((ref) => { const key = `${ref.anglerId}:${ref.rod}`; const active = copyTargets.includes(key); return <button key={key} type="button" onClick={() => setCopyTargets((current) => active ? current.filter((item) => item !== key) : [...current, key])} className={`min-h-10 rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? "border-accent bg-accent text-accent-fg" : "border-border"}`}>{ref.label}</button>; })}</div>
                <Button className="min-h-12 w-full" disabled={!copyTargets.length} onClick={copyRod} isLoading={busy}>Copy tackle setup</Button>
              </div>
            ) : null}
          </div>
        </section>
      ) : (
        <>
          {showPostCatch ? (
            <section className="rounded-xl border border-accent/50 bg-accent/5 p-4">
              <p className="font-semibold text-primary">Rod {caughtRod} is back out. What changed?</p>
              <p className="mt-1 text-sm text-secondary">If nothing changed, one tap keeps every exposure clock running.</p>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <Button className="min-h-11" asChild><Link to={`/live/${id}`}>Back out unchanged</Link></Button>
                <Button className="min-h-11" variant="secondary" onClick={() => openEditor("hookbait", caughtAngler, caughtRod)}>Changed hookbait</Button>
                <Button className="min-h-11" variant="secondary" onClick={() => openEditor("rig", caughtAngler, caughtRod)}>Changed rig</Button>
                <Button className="min-h-11" variant="secondary" onClick={() => openEditor("position", caughtAngler, caughtRod)}>Move</Button>
                <Button className="min-h-11" variant="secondary" onClick={() => openEditor("feed", caughtAngler, caughtRod)}>Top up spot</Button>
              </div>
            </section>
          ) : null}

          <section className="space-y-3" aria-label="Live rods">
            {rodRefs.map((ref) => {
              const key = `${ref.anglerId}:${ref.rod}`;
              const leadState = stateMap.get(stateKey(ref.anglerId, ref.rod, "lead_system"));
              const rigState = stateMap.get(stateKey(ref.anglerId, ref.rod, "rig"));
              const hookState = stateMap.get(stateKey(ref.anglerId, ref.rod, "hookbait"));
              const position = positionMap.get(key);
              const feed = latestSpotFeedMap.get(key);
              const setup = setupMap.get(key);
              const feedDetail = feed?.occurred_at
                ? `${feed.action_type === "top_up" ? "Last topped up" : "Last fed"} ${feedAge(feed.occurred_at)}`
                : undefined;
              return (
                <article key={key} className="overflow-hidden rounded-2xl border border-border bg-raised">
                  <div className="flex items-center justify-between gap-3 border-b border-border px-3.5 py-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-bold text-primary">{ref.label}</h2>
                      <p className="text-xs text-secondary">Fishing now</p>
                    </div>
                    <Button className="min-h-11" size="sm" variant="tertiary" onClick={() => openEditor("copy", ref.anglerId, ref.rod)}><Copy className="size-4" /> Copy setup</Button>
                  </div>

                  <div className="space-y-3 p-3">
                    <div>
                      <SectionLabel>Position</SectionLabel>
                      <EditableStateRow
                        icon={<MapPin className="size-4" />}
                        label="Current spot"
                        value={position?.spot_snapshot || "Position not set"}
                        actionLabel="Move"
                        emphasis
                        onClick={() => openEditor("position", ref.anglerId, ref.rod)}
                      />
                    </div>

                    <div>
                      <SectionLabel>Setup</SectionLabel>
                      <div className="overflow-hidden rounded-xl border border-border bg-background">
                        <EditableStateRow compact label="Lead" value={leadState?.snapshot} actionLabel="Change" onClick={() => openEditor("lead", ref.anglerId, ref.rod)} />
                        <EditableStateRow compact divided label="Rig" value={rigState?.snapshot || setup?.rig_snapshot} actionLabel="Change" onClick={() => openEditor("rig", ref.anglerId, ref.rod)} />
                        <EditableStateRow compact divided label="Hookbait" value={hookState?.snapshot || setup?.hookbait_snapshot} actionLabel="Change" onClick={() => openEditor("hookbait", ref.anglerId, ref.rod)} />
                      </div>
                    </div>

                    <div>
                      <SectionLabel>Feed</SectionLabel>
                      <EditableStateRow
                        label="Spot feed"
                        value={feed ? feedSummary(feed) || "Feed recorded" : "No feed recorded on this spot"}
                        detail={feedDetail}
                        actionLabel="Top up"
                        onClick={() => openEditor("feed", ref.anglerId, ref.rod)}
                      />
                    </div>
                  </div>

                  <div className="border-t border-border bg-background/35 px-3 py-3">
                    <SectionLabel>Log an event</SectionLabel>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <Link
                        to={`/catches/new?sessionId=${id}&anglerId=${ref.anglerId}&rodNumber=${ref.rod}${setup?.id ? `&setupId=${setup.id}` : ""}&quick=1`}
                        className="col-span-2 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-accent px-3 py-2 text-center text-sm font-bold text-accent-fg transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus-ring/50"
                      >
                        <Fish className="size-4" /> Caught
                      </Link>
                      <EventAction label="Aborted take" disabled={busy} onClick={() => quickEvent(ref.anglerId, ref.rod, "aborted_take")} />
                      <EventAction label="Lost fish" disabled={busy} onClick={() => quickEvent(ref.anglerId, ref.rod, "lost_fish")} />
                      <EventAction label="Knock" disabled={busy} onClick={() => quickEvent(ref.anglerId, ref.rod, "knock")} />
                      <EventAction label="Liner" disabled={busy} onClick={() => quickEvent(ref.anglerId, ref.rod, "liner")} />
                      <EventAction label="Recast" disabled={busy} onClick={() => quickEvent(ref.anglerId, ref.rod, "recast")} />
                    </div>
                  </div>
                </article>
              );
            })}
          </section>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button className="min-h-11" asChild variant="secondary"><Link to={`/sessions/${id}`}>Session details / End session</Link></Button>
            <Button className="min-h-11" asChild variant="secondary"><Link to={`/advisor?sessionId=${id}`}>Ask CatchCompass <ChevronRight /></Link></Button>
          </div>
        </>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-tertiary">{children}</p>;
}

function EditableStateRow({
  label,
  value,
  detail,
  actionLabel,
  onClick,
  icon,
  compact = false,
  divided = false,
  emphasis = false,
}: {
  label: string;
  value?: string | null;
  detail?: string | null;
  actionLabel: string;
  onClick: () => void;
  icon?: React.ReactNode;
  compact?: boolean;
  divided?: boolean;
  emphasis?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-3 text-left transition-colors hover:bg-inset focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-focus-ring/50 ${compact ? "min-h-12 py-2" : "min-h-14 rounded-xl border py-2.5"} ${divided ? "border-t border-border" : ""} ${!compact && emphasis ? "border-accent/35 bg-accent/5" : !compact ? "border-border bg-background" : ""}`}
    >
      {icon ? <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-inset text-accent">{icon}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-secondary">{label}</span>
        <span className="mt-0.5 line-clamp-2 break-words text-sm font-semibold leading-5 text-primary">{value || "Not set"}</span>
        {detail ? <span className="mt-0.5 block text-[11px] text-secondary">{detail}</span> : null}
      </span>
      <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-accent">
        {actionLabel}<ChevronRight className="size-4" />
      </span>
    </button>
  );
}

function EventAction({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="min-h-12 rounded-xl border border-border bg-inset px-2 py-2 text-center text-xs font-semibold leading-4 text-primary transition-colors hover:bg-border disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus-ring/50"
    >
      {label}
    </button>
  );
}

function PageMessage({ text }: { text: string }) {
  return <div className="mx-auto max-w-[760px] px-4 py-8 text-sm text-secondary">{text}</div>;
}
